import React, { useEffect, useState } from "react";
import Cookie from "js-cookie";
import { Modal } from "@clarkinc/zest-design-system";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import getMinBuyMustBuyText from "../getMinBuyMustBuyText";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import OptionalCustomizationButton from "./OptionalCustomizationButton";
import QuantityDropdown from "./QuantityDropdown";
import addToCartRequest from "./request";
import RequiredCustomizationButton from "./RequiredCustomizationButton";
import { getValidationError } from "./validations";
import ProductWarningModal from "~/common/AddToCartModal/ProductWarningModal";
import CommercialUseOnlyModal from "~/common/AddToCartModal/CommercialUseOnlyModal";
import useAddToCartModals from "~/common/AddToCartModal/useAddToCartModals";
import { AddToCartModalNames, WarrantyOption } from "~/common/AddToCartModal";
import { User } from "~/common/Store/models/User";
import ValidationError from "./ValidationError";
import WhatWeOffer from "./WhatWeOffer";
import Accessories, { AccessorySelections } from "./Accessories";
import {
  AutoReorder,
  ExtendedWarranty,
} from "~/ProductDetails/ProductDetailPageResult";
import Notification from "~/common/Notification";
import addToExistingAutoReorderRequest, {
  AddToExistingAutoReorderResponse,
} from "./existingAutoReorderRequest";
import isGroupMemberSelected from "../isGroupMemberSelected";
import ReplacementButton from "./ReplacementButton";
import CommonCarrierSettingsModal from "./CommonCarrierSettingsModal";
import getCustomizableProductUrl from "~/common/helpers/getCustomizableProductUrl";
import CartLimitModal from "./CartLimitModal";
import ExtendedWarrantyModalContext from "./WhatWeOffer/ExtendedWarranty/ExtendedWarrantyModalContext";
import { getSelectedOptionAndSendGtmEvent } from "~/userAnalytics";
import {
  clearGa4EventInDataLayer,
  sendGtmEvent,
} from "~/analytics/definitions";

interface Props {
  product: PricingProduct;
  user: User;
  totalNumOfItemsAddedToCart: number;
  setTotalNumOfItemsAddedToCart: React.Dispatch<React.SetStateAction<number>>;
  handleAddToCartError: (validationError: ValidationError | undefined) => void;
  isGoogleView?: boolean;
  setAddToCartSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  autoReorder?: AutoReorder;
  handleSetErrorMessage: (errorMessage: string) => void;
  quantity: string;
  hasQuantitySelect: boolean;
  handleAddToCartQuantityChange: (quantity: string) => void;
  previouslyPurchasedOrderNumber: number;
}

const sendGtmOptionEventsOnCheckout = (
  accessorySelections: Readonly<AccessorySelections>,
  rootNode: HTMLElement | Document = document,
) => {
  Object.values(accessorySelections).forEach((accessoryValue) => {
    getSelectedOptionAndSendGtmEvent(
      accessoryValue,
      "PDP Accessory ATC",
      rootNode,
    );
  });
};

const getVirtualGroupingProductOptions = () => {
  // Get all the product options
  const productOptionSelects: NodeListOf<HTMLSelectElement> =
    document.querySelectorAll(".virtual-grouping .grouping-select");
  productOptionSelects.forEach((productSelectOption) => {
    const text = productSelectOption.selectedOptions[0]?.innerText;
    if (text) {
      clearGa4EventInDataLayer();
      sendGtmEvent({
        event: "ga4_event",
        eventName: "PDP Options ATC",
        eventAction: "PDP Options ATC",
        eventLabel: text,
      });
    }
  });
};

const AddToCart = ({
  product,
  user,
  totalNumOfItemsAddedToCart,
  setTotalNumOfItemsAddedToCart,
  handleAddToCartError,
  isGoogleView = false,
  autoReorder = {
    isToggleEnabled: false,
    isAddToExistingAutoReorder: false,
    newAutoReorderInterval: "0",
    selectedExistingAutoReorder: {
      name: "",
      isActive: false,
      creditCardId: 0,
      desiredOrderDate: "",
      id: 0,
    },
  },
  setAddToCartSuccess,
  handleSetErrorMessage,
  quantity,
  hasQuantitySelect,
  handleAddToCartQuantityChange,
  previouslyPurchasedOrderNumber,
}: Props) => {
  const {
    minimumBuy,
    mustBuy,
    unitOfMeasure,
    isCustomizable,
    customizationUrl,
    itemNumber,
    warningContent,
    accessories,
    isThirdPartyExtendedWarrantyEligible,
    extendedWarranties,
    price,
    feedIdentifier,
    productReplacement,
    isReplacementEligible,
    isInStock,
  } = product;

  const startBuy = Math.max(minimumBuy, mustBuy);
  const hasSelectedGroupMember = isGroupMemberSelected(product);

  // State allows unrelated controls to affect whether the Warranty Modal displays the option to stop showing upsell warranty modals when adding to cart
  const [displayWarrantyOffer, setDisplayWarrantyOffer] = useState<
    boolean | null
  >(false);
  const [runAddToCartModalFunctions, setRunAddToCartModalFunctions] =
    useState<boolean>(false);

  const [accessorySelections, setAccessorySelections] =
    useState<AccessorySelections>({});
  const [invalidAccessories, setInvalidAccessories] = useState<string[]>([]);
  const [warrantyPlanId, setWarrantyPlanId] = useState(0);
  const minMustBuyText = getMinBuyMustBuyText(
    minimumBuy,
    mustBuy,
    unitOfMeasure,
    isGoogleView,
  );
  const [
    addToExistingAutoReorderResponse,
    setAddToExistingAutoReorderResponse,
  ] = useState<AddToExistingAutoReorderResponse>({
    hasResidential: false,
    customerLiftgateFee: 0,
    isChangedToCommonCarrier: false,
  });

  const [installationServiceIds, setInstallationServiceIds] = useState<
    number[]
  >([]);

  /* For Modal warning */
  const [hasAcknowledgedProductWarning, setHasAcknowledgedProductWarning] =
    useState(false);

  const [
    hasAcknowledgedCommercialUseWarning,
    setHasAcknowledgedCommercialUseWarning,
  ] = useState(false);

  const [isInternationalUser, setIsInternationalUser] = useState(false);

  useEffect(() => {
    const checkIfUserHasInternationalAddress = () => {
      return (
        !!user.facebookUserData.country &&
        !(
          user.facebookUserData.country === "US" ||
          user.facebookUserData.country === "United States"
        )
      );
    };

    setIsInternationalUser(checkIfUserHasInternationalAddress());
  }, [user.facebookUserData.country]);

  const doNotDisplayWarrantyOffer =
    Cookie.get("doNotShowWarrantyModal") === "true";

  const hasCommercialAddress = user.registered && user.isCommercial;
  const warranties: ExtendedWarranty[] = extendedWarranties;

  const showWarrantyModal =
    !!warranties &&
    warranties.length > 0 &&
    !doNotDisplayWarrantyOffer &&
    hasSelectedGroupMember &&
    !isInternationalUser;

  const showCommercialUseModal = showWarrantyModal && !hasCommercialAddress;

  const showOptionalCustomizationButton = isCustomizable || customizationUrl;

  const displayRequiredCustomization =
    shouldDisplayRequiredCustomization(product);

  const handleQuantityChange = (value: string) => {
    let quantityValue = value;

    if (quantityValue !== "" && Number.isNaN(quantityValue)) {
      quantityValue = startBuy.toString();
    }

    handleSetErrorMessage("");
    handleAddToCartQuantityChange(quantityValue);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(event.target.value);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [cartLimitMessage, setCartLimitMessage] = useState("");

  const openCartLimitModal = (errorMessage: string) => {
    setCartLimitMessage(errorMessage);
    setModalOpen(true);
  };

  interface AddToCartArgs {
    hasAcknowledgedProductWarning: boolean;
    hasAcknowledgedCommercialUseWarning: boolean;
    hasCommercialAddress: boolean;
    openModals?: (modals: AddToCartModalNames[]) => void;
    selectedWarrantyPlanId: number;
  }

  const AddToCart = async ({
    openModals,
    hasAcknowledgedProductWarning,
    hasAcknowledgedCommercialUseWarning,
    hasCommercialAddress,
    selectedWarrantyPlanId = 0,
  }: AddToCartArgs) => {
    const validationError = getValidationError({
      product,
      quantity: Number(quantity),
      autoReorderSelections: autoReorder,
      accessorySelections,
      setInvalidAccessories,
    });

    handleSetErrorMessage(validationError?.message ?? "");

    if (validationError && validationError.message?.length > 0) {
      handleAddToCartError(validationError);
      const buyBox = document.querySelector("#priceBox");

      if (buyBox) {
        const { top, bottom, height } = buyBox.getBoundingClientRect();
        const { pageYOffset } = window;

        // if element is not in viewport
        if (top <= pageYOffset || top + height >= bottom + pageYOffset) {
          buyBox.scrollIntoView();
        }
      }

      return;
    }

    if (openModals) {
      const modalsToOpen: AddToCartModalNames[] = [];
      const hasWarning = !!warningContent && warningContent.length > 0;

      if (hasWarning && !hasAcknowledgedProductWarning) {
        modalsToOpen.push("warning");
      }

      if (
        showWarrantyModal &&
        !selectedWarrantyPlanId &&
        !hasAcknowledgedCommercialUseWarning &&
        ((isThirdPartyExtendedWarrantyEligible &&
          (!user.registered || hasCommercialAddress)) ||
          !isThirdPartyExtendedWarrantyEligible)
      ) {
        modalsToOpen.push("warranty");
      }

      if (showCommercialUseModal && !hasAcknowledgedCommercialUseWarning) {
        modalsToOpen.push("commercialUse");
      }

      if (modalsToOpen.length > 0) {
        openModals(modalsToOpen);
        if (modalsToOpen.includes("warranty") && !doNotDisplayWarrantyOffer) {
          setDisplayWarrantyOffer(true);
          setRunAddToCartModalFunctions(true);
        }
        return;
      }
    }

    if (autoReorder.isAddToExistingAutoReorder && autoReorder.isToggleEnabled) {
      await addToExistingAutoReorderRequest({
        product: { ...product, price: product.price.price },
        quantity: Number(quantity),
        existingAutoReorderId:
          autoReorder.selectedExistingAutoReorder.id.toString(),
        setAddToExistingAutoReorderResponse,
      }).then(() => {
        new Notification({
          imageUrl:
            product.productImages[0]?.thumbnail ||
            "/images/products/new/imagecomingsoon_tn.jpg",
          heading: `${quantity} item${
            quantity !== "1" ? `s` : ``
          } added to your ${
            autoReorder.selectedExistingAutoReorder.name.length > 0
              ? autoReorder.selectedExistingAutoReorder.name
              : `Auto Reorder #${autoReorder.selectedExistingAutoReorder.id}`
          }`,
          href: `/myaccount:autoreorder/detail/?r=${autoReorder.selectedExistingAutoReorder.id}`,
          body: "",
          buttonText: "View Auto Reorder",
        });
        clearGa4EventInDataLayer();
        sendGtmEvent({
          event: "ga4_event",
          eventName: "PDP Auto Reorder",
          eventAction: "Auto Reorder ATC - Existing",
          eventLabel: `${product.itemNumber}:${autoReorder.autoReorderDropdownValue}`,
        });
      });
    } else {
      try {
        const success = await addToCartRequest({
          product: { ...product, price: product.price.price },
          quantity: Number(quantity),
          autoReorderInterval:
            autoReorder.isToggleEnabled &&
            !autoReorder.isAddToExistingAutoReorder
              ? autoReorder.newAutoReorderInterval
              : "0",
          serviceTierPricingIds: installationServiceIds.map((x) => x),
          accessoryIds: Object.values(accessorySelections).filter((x) => x > 0),
          warrantyPlanId:
            !hasCommercialAddress && !hasAcknowledgedCommercialUseWarning
              ? 0
              : selectedWarrantyPlanId,
          openCartLimitModal,
          gtmAtcProps: { source: "product detail" },
        });

        if (Number(autoReorder.newAutoReorderInterval) > 0) {
          clearGa4EventInDataLayer();
          sendGtmEvent({
            event: "ga4_event",
            eventName: "PDP Auto Reorder",
            eventAction: "Auto Reorder ATC - New",
            eventLabel: `${product.itemNumber}:${autoReorder.autoReorderDropdownValue}`,
          });
        }

        if (success) {
          sendGtmOptionEventsOnCheckout(accessorySelections);
          getVirtualGroupingProductOptions();
          setAddToCartSuccess(true);
          setTotalNumOfItemsAddedToCart(
            totalNumOfItemsAddedToCart + Number(quantity),
          );
        } else {
          handleSetErrorMessage(
            "There was an error adding this item to your cart.",
          );
        }
      } catch (error) {
        handleSetErrorMessage(
          "There was an error adding this item to your cart.",
        );
      }
    }
  };

  const { openModals, activeModal, closeModal, submitModal } =
    useAddToCartModals({
      onComplete: async (results, openModals) => {
        let warrantyId =
          (results.warranty?.warrantyId ?? 0) ||
          !results.warranty?.noThanksSelected
            ? warrantyPlanId
            : 0;

        let skipCommercialUseModal =
          !!results.warranty?.skipCommercialModal ||
          !!results.commercialUse ||
          warrantyId === 0;

        if (!results.commercialUse && !hasCommercialAddress) {
          warrantyId = 0;
          skipCommercialUseModal = true;
        }
        setHasAcknowledgedCommercialUseWarning(skipCommercialUseModal);
        setWarrantyPlanId(warrantyId);

        AddToCart({
          openModals,
          hasAcknowledgedProductWarning: true,
          hasAcknowledgedCommercialUseWarning: skipCommercialUseModal,
          hasCommercialAddress: !!hasCommercialAddress,
          selectedWarrantyPlanId: warrantyId,
        });
      },
    });

  const handleAddToCart = (event: React.SyntheticEvent) => {
    event.preventDefault();

    let skipCommercialModal = hasAcknowledgedCommercialUseWarning;
    if (
      user.registered &&
      !hasCommercialAddress &&
      warrantyPlanId === 0 &&
      isThirdPartyExtendedWarrantyEligible
    ) {
      skipCommercialModal = true;
    }

    return AddToCart({
      openModals,
      hasAcknowledgedProductWarning,
      hasAcknowledgedCommercialUseWarning: skipCommercialModal,
      hasCommercialAddress: !!hasCommercialAddress,
      selectedWarrantyPlanId: warrantyPlanId,
    });
  };

  const isProductWarningModalOpen = activeModal === "warning";
  const isCommercialUseOnlyModalOpen = activeModal === "commercialUse";
  if (activeModal === "warranty") {
    Modal.open("extended-warranty-modal-dialog");
  }

  const ProductModals = () => (
    <>
      {warningContent && isProductWarningModalOpen && (
        <ProductWarningModal
          {...{
            warningContent,
            isOpen: isProductWarningModalOpen,
            setHasAcknowledgedProductWarning,
            closeModal,
            submitModal,
          }}
        />
      )}
      {showCommercialUseModal && isCommercialUseOnlyModalOpen && (
        <CommercialUseOnlyModal
          {...{
            isOpen: isCommercialUseOnlyModalOpen,
            onClose: () => closeModal(),
            onSubmit: (result) => submitModal({ commercialUse: result }),
            submitModal,
            closeModal,
          }}
        />
      )}
    </>
  );

  const handleSubmit = (result: WarrantyOption) => {
    setWarrantyPlanId(result.warrantyId);
    setHasAcknowledgedCommercialUseWarning(!!result.skipCommercialModal);
  };

  return (
    <>
      <CartLimitModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        cartLimitMessage={cartLimitMessage}
      />
      <ProductModals />
      {isReplacementEligible && (
        <ReplacementButton
          replacementLink={productReplacement.replacementLink}
        />
      )}
      {user.registered && product.isAutomaticReorderEligible && (
        <CommonCarrierSettingsModal
          existingAutoReorder={autoReorder.selectedExistingAutoReorder}
          addToExistingAutoReorderResponse={addToExistingAutoReorderResponse}
          setAddToExistingAutoReorderResponse={
            setAddToExistingAutoReorderResponse
          }
        />
      )}
      {displayRequiredCustomization && (
        <RequiredCustomizationButton
          customizationUrl={getCustomizableProductUrl(
            product.customizationUrl,
            itemNumber,
          )}
          handleSetErrorMessage={handleSetErrorMessage}
          handleAddToCartError={handleAddToCartError}
          product={product}
        />
      )}
      {!displayRequiredCustomization && !isReplacementEligible && (
        <>
          <div className="add-to-cart">
            <form id="productform" action="/viewcart.cfm" method="post">
              <input type="hidden" name="from" value="add" />
              <input type="hidden" name="price" value={price?.price || 0} />
              <input type="hidden" name="mnbuy" value={minimumBuy} />
              <input type="hidden" name="mxbuy" value={mustBuy} />
              <input
                type="hidden"
                name="feed_identifier"
                value={feedIdentifier}
              />
              <input
                type="hidden"
                name="item_number"
                value={itemNumber?.toLocaleUpperCase()}
              />
              {accessories?.length > 0 && (
                <Accessories
                  product={product}
                  invalidAccessories={invalidAccessories}
                  accessorySelections={accessorySelections}
                  setAccessorySelections={setAccessorySelections}
                  handleAddToCartError={handleAddToCartError}
                  handleSetErrorMessage={handleSetErrorMessage}
                />
              )}
              <ExtendedWarrantyModalContext.Provider
                value={{
                  showDoNotDisplayOfferButton: displayWarrantyOffer,
                  setShowDoNotDisplayOfferButton: setDisplayWarrantyOffer,
                  runAddToCartModalFunctions,
                  setRunAddToCartModalFunctions,
                  addToCartCloseModal: closeModal,
                  addToCartSubmitModal: (result) =>
                    submitModal({
                      warranty: result,
                    }),
                }}
              >
                <WhatWeOffer
                  product={product}
                  user={user}
                  quantity={Number(quantity)}
                  selectedWarranty={product.extendedWarranties?.find(
                    (x) => x.id === warrantyPlanId,
                  )}
                  setInstallationServiceIds={(ids: number[]) => {
                    setInstallationServiceIds(ids);
                  }}
                  onSubmit={handleSubmit}
                  handleAddToCartError={handleAddToCartError}
                  handleSetErrorMessage={handleSetErrorMessage}
                  isInternationalUser={isInternationalUser}
                />
              </ExtendedWarrantyModalContext.Provider>
              <div id="addToCartContainer" className="relative z-0">
                <div
                  className={`btn-container${
                    hasQuantitySelect ? " special-quantity" : ""
                  }`}
                  data-component={`${
                    hasQuantitySelect ? "specialQuantity" : "customQuantity"
                  }`}
                >
                  {minMustBuyText.length > 0 && (
                    <p className="min-must-text">{minMustBuyText}</p>
                  )}
                  {hasQuantitySelect && (
                    <QuantityDropdown
                      quantity={quantity}
                      minBuy={minimumBuy}
                      mustBuy={mustBuy}
                      unitOfMeasure={unitOfMeasure}
                      handleQuantityChange={handleQuantityChange}
                    />
                  )}
                  {!hasQuantitySelect && isInStock && (
                    <input
                      autoComplete="off"
                      type="number"
                      className="quantity"
                      name="qty"
                      id="qty"
                      value={quantity}
                      aria-describedby={
                        Number(quantity) < 1 ? "errorMessageId" : undefined
                      }
                      aria-label="Quantity"
                      onChange={handleOnChange}
                    />
                  )}
                  {isInStock && (
                    <input
                      gtm-id={
                        previouslyPurchasedOrderNumber > 0
                          ? "BuyItAgainATC"
                          : "AddToCartATC"
                      }
                      type={hasSelectedGroupMember ? "submit" : "button"}
                      id="buyButton"
                      className="btn btn-cart btn-large"
                      value={
                        autoReorder.isAddToExistingAutoReorder &&
                        autoReorder.isToggleEnabled
                          ? "Add to Auto Reorder"
                          : "Add to Cart"
                      }
                      onClick={handleAddToCart}
                    />
                  )}
                </div>
              </div>
            </form>
          </div>
          {showOptionalCustomizationButton && (
            <OptionalCustomizationButton
              customizationUrl={getCustomizableProductUrl(
                product.customizationUrl,
                itemNumber,
              )}
              handleSetErrorMessage={handleSetErrorMessage}
              handleAddToCartError={handleAddToCartError}
              product={product}
            />
          )}
        </>
      )}
    </>
  );
};

export default AddToCart;
